import {Injectable, NgZone, inject} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, mergeMap, shareReplay, tap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {AppConfig} from '../../_data/config/app-config';

export interface MapGeocoderResponse {
  status: google.maps.GeocoderStatus;
  results: google.maps.GeocoderResult[];
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {

  /** True only when a Google Maps key is configured in config.json */
  readonly isEnabled: boolean;
  readonly loadApi$: Observable<boolean>;

  private readonly httpClient = inject(HttpClient);
  private readonly _ngZone = inject(NgZone);
  private readonly appConfig = inject(AppConfig);
  private readonly apiKey: string;
  private _geocoder: google.maps.Geocoder;

  constructor() {
    this.apiKey = this.appConfig?.googleMapsApiKey?.trim() ?? '';
    this.isEnabled = !!this.apiKey;
    this.loadApi$ = this.isEnabled ? this.createApiLoader() : of(false);
  }

  private createApiLoader(): Observable<boolean> {
    console.debug('_loadApi()');
    return this.httpClient.jsonp(`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey)}`, 'callback')
      .pipe(
        map(() => {
          console.debug('loadApi() successful');
          //Create a Geocoder instance from the now loaded API js
          this._geocoder = new google.maps.Geocoder();
          return true;
        }),
        catchError(err => {
          console.error('loadApi() failure', err);
          return of(false);
        }),
        //Use share replay so that other subscribers don't trigger loading the API over and over again
        shareReplay(1)
      );
  }

  geocodeAddress(address: string): Observable<MapGeocoderResponse> {
    console.log(`Start geocoding! - ${address}`);

    //First check if the api is loaded, then return the observable that will do the geocoding
    return this.loadApi$.pipe(
      tap(loaded => console.debug('API loaded?', loaded)),
      mergeMap(loaded => {
        if(!loaded || !this._geocoder) {
          return throwError(() => new Error(this.isEnabled ? 'Google Maps API failed to load' : 'Google Maps is disabled (no API key configured)'));
        }
        //The code itself comes from here: https://github.com/angular/components/tree/master/src/google-maps/map-geocoder
        return new Observable<MapGeocoderResponse>(observer => {
          this._geocoder.geocode({address: address}, (results, status) => {
            this._ngZone.run(() => {
              observer.next({results, status});
              observer.complete();
            });
          });
        });
      })
    );
  }

}
